import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['fileInput', 'status', 'dropZone']

  connect() {
    // Controller is ready
  }

  triggerFileSelect() {
    this.fileInputTarget.click()
  }

  // Drag and drop handlers
  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = 'copy'
    this.dropZoneTarget.classList.add('border-blue-500', 'bg-blue-50')
    this.dropZoneTarget.classList.remove('border-neutral-900', 'bg-slate-50')
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove('border-blue-500', 'bg-blue-50')
    this.dropZoneTarget.classList.add('border-neutral-900', 'bg-slate-50')
  }

  async handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()

    // Reset visual state
    this.dropZoneTarget.classList.remove('border-blue-500', 'bg-blue-50')
    this.dropZoneTarget.classList.add('border-neutral-900', 'bg-slate-50')

    const files = event.dataTransfer.files
    if (files.length === 0) return

    await this.importFile(files[0])
  }

  async handleFileSelect(event) {
    const file = event.target.files[0]
    if (!file) return

    await this.importFile(file)

    // Reset file input so same file can be selected again
    event.target.value = ''
  }

  async importFile(file) {
    if (!file.name.endsWith('.json')) {
      this.showStatus('Please select a JSON file', 'error')
      return
    }

    try {
      const text = await file.text()
      const data = JSON.parse(text)
      this.populateForm(data)
      this.showStatus(`Imported "${data.title || 'article'}" successfully!`, 'success')
    } catch (e) {
      this.showStatus(`Import failed: ${e.message}`, 'error')
    }
  }

  populateForm(data) {
    const form = this.element

    // Map JSON fields to form field names
    const fieldMappings = {
      'title': 'resource_article[title]',
      'slug': 'resource_article[slug]',
      'excerpt': 'resource_article[excerpt]',
      'status': 'resource_article[status]',
      'evidence_level': 'resource_article[evidence_level]',
      'author_name': 'resource_article[author_name]',
      'featured': 'resource_article[featured]',
      'video_embed_url': 'resource_article[video_embed_url]',
      'meta_title': 'resource_article[meta_title]',
      'meta_description': 'resource_article[meta_description]',
      'custom_canonical_url': 'resource_article[custom_canonical_url]',
      'schema_type': 'resource_article[schema_type]',
      'noindex': 'resource_article[noindex]'
    }

    // Populate text/select fields
    Object.entries(fieldMappings).forEach(([jsonKey, formName]) => {
      if (data[jsonKey] !== undefined) {
        const field = form.querySelector(`[name="${formName}"]`)
        if (field) {
          if (field.type === 'checkbox') {
            field.checked = !!data[jsonKey]
          } else {
            field.value = data[jsonKey]
          }
        }
      }
    })

    // Handle resource_category_id specially - try to match by name if UUID is placeholder
    if (data.resource_category_id) {
      const categoryField = form.querySelector('[name="resource_article[resource_category_id]"]')
      if (categoryField) {
        // Check if it's a valid UUID format or a placeholder
        const isValidUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data.resource_category_id)
        if (isValidUuid) {
          categoryField.value = data.resource_category_id
        }
        // If it's a placeholder like "uuid-of-category", leave the current selection
      }
    }

    // Handle content_blocks - update the hidden input and trigger Vue editor refresh
    if (data.content_blocks && Array.isArray(data.content_blocks)) {
      this.updateContentBlocks(data.content_blocks)
    }
  }

  updateContentBlocks(blocks) {
    // Find the content blocks editor controller element
    const editorElement = this.element.querySelector('[data-controller*="content-blocks-editor"]')
    if (!editorElement) return

    // Update the hidden input
    const hiddenInput = editorElement.querySelector('[data-admin--content-blocks-editor-target="hiddenInput"]')
    if (hiddenInput) {
      hiddenInput.value = JSON.stringify(blocks)
    }

    // Update the JSON fallback textarea
    const jsonFallback = editorElement.querySelector('[data-admin--content-blocks-editor-target="jsonFallback"]')
    if (jsonFallback) {
      jsonFallback.value = JSON.stringify(blocks, null, 2)
    }

    // Get the Stimulus controller instance and update its blocks value to trigger Vue remount
    const controller = this.application.getControllerForElementAndIdentifier(
      editorElement,
      'admin--content-blocks-editor'
    )

    if (controller) {
      controller.blocksValue = blocks
      controller.mountVueEditor()
    }
  }

  showStatus(message, type) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.remove('hidden', 'text-green-600', 'text-red-600')

    if (type === 'success') {
      this.statusTarget.classList.add('text-green-600')
    } else if (type === 'error') {
      this.statusTarget.classList.add('text-red-600')
    }

    // Auto-hide success messages after 5 seconds
    if (type === 'success') {
      setTimeout(() => {
        this.statusTarget.classList.add('hidden')
      }, 5000)
    }
  }
}
